import React, {useContext, useEffect, useMemo} from "react"
import {TabbedPageHeader} from "../../../../components/TabbedPageHeader";
import {ProviderProfileContext} from "../../../../ProviderProfileProvider";
import {
  com,
  IApiBusinessProto,
  space,
  UpdatedBusinessResponse,
  UpdateIntendedMembershipRequest
} from "../../../../provider_api";
import './Membership.css';
import {activeSubscriptionToString} from "../../../../util/ProviderUtil";
import {useNewSubscriptionLink} from "../../../../util/RpcHooks/GetNewSubscriptionLink";
import {useManageSubscriptionLink} from "../../../../util/RpcHooks/GetManageSubscriptionLink";
import {useStripeMembershipStatusRpc} from "../../../../util/RpcHooks/GetStripeMembershipStatus";
import {useSettingsTabs} from "./SettingsRouter";
import {ActionButton} from "../../../../util/UI/ActionButton";
import {KensuObjectEditor} from "../../../../components/table/KensuObjectEditor";
import {EditableSingleSelect} from "../../../../components/Editable/EditableSingleSelect";
import {EditableStringField} from "../../../../components/Editable/EditableStringField";
import ProviderSubscriptionRequest = com.onkensu.platform.billing.ProviderSubscriptionRequest;
import BusinessSubscriptionType = space.kenko.proto.BusinessSubscriptionType;
import BusinessSubscriptionProto = space.kenko.proto.BusinessSubscriptionProto;
import IBusinessSubscriptionProto = space.kenko.proto.IBusinessSubscriptionProto;

export const Membership = () => {

  const {business, setBusiness} = useContext(ProviderProfileContext);

  const getMembershipRpc = useStripeMembershipStatusRpc();
  const getNewSubscriptionLink = useNewSubscriptionLink();
  const getManageSubscriptionLink = useManageSubscriptionLink();

  useEffect(() => {
    getMembershipRpc.send();
  }, []);

  const newSubscription = () => {
    getNewSubscriptionLink.send(new ProviderSubscriptionRequest({
      subscription: business?.subscription?.type,
      quantity: business?.subscription?.quantity
    }));
  }

  const manageSubscription = () => {
    getManageSubscriptionLink.send();
  }

  // This assumes an active membership.
  const SubscriptionStatus = () => {

    let membership = getMembershipRpc.membership;

    return <>

      <div className={'GreenHighlight'}>
        Your subscription for <b>{activeSubscriptionToString(membership)}</b> is active
      </div>

      <br/><br/>
      You may manage or upgrade your membership by clicking the button below.
      <br/><br/>
      <div>
        <ActionButton onClick={manageSubscription} rpcState={getManageSubscriptionLink.rpcState}>
          Manage membership
        </ActionButton>
      </div>
      {getManageSubscriptionLink.rpcState.state == 'error' &&
          <div className={'ErrorText'}> There was an error. Please try again. </div>
      }
    </>
  }

  const IntendedMembershipEditor = () => {
    return <KensuObjectEditor<IBusinessSubscriptionProto, IApiBusinessProto>
        idParam={'singleton'}
        getter={() => business?.subscription}
        getTitle={() => 'intentioned subscription'}
        headerName={'My membership'}
        hideHeaderInEditMode={true}
        editLabel={"Edit"}
        entityName={"Membership"}
        entityListViewLink={""}
        newBlankType={() => new BusinessSubscriptionProto()}
        getRpcMethod={() => 'update_intended_membership'}
        getRpcPayload={sub => UpdateIntendedMembershipRequest.encode(
            new UpdateIntendedMembershipRequest({
              quantity: sub.quantity,
              type: sub.type,
            })).finish()}
        parseRpcResponse={UpdatedBusinessResponse.decode}
        handleContainerTypeUpdate={setBusiness}>

      <EditableSingleSelect<IBusinessSubscriptionProto, BusinessSubscriptionType>
          field={'type'}
          label={'Membership type'}
          options={[
            {label: 'Kensu Starter', value: BusinessSubscriptionType.LISTING},
            {label: 'Kensu Growth', value: BusinessSubscriptionType.LISTING_PLUS_BOOKING}
          ]}
          helpText={(isEditing, workingCopy) => {
            if (workingCopy.type == BusinessSubscriptionType.LISTING) {
              return 'This is the most basic membership. It includes a listing of your business, ' +
                  'but no booking features.'
            } else if (workingCopy.type == BusinessSubscriptionType.LISTING_PLUS_BOOKING) {
              return 'This is the most advanced membership. It includes a listing of your business, ' +
                  'and booking features. You can add classes and appointments to your listing ' +
                  'via the Calendar. You can also add availability blocks to your calendar, ' +
                  'and define classes and appointments via the Catalog.'
            } else {
            }
          }}
      />

      <EditableStringField<IBusinessSubscriptionProto>
          inputType={'number'}
          label={workingCopy => workingCopy.type == BusinessSubscriptionType.LISTING ?
              'Number of locations' : 'Number of practitioners'}
          field={'quantity'}
      />

      <br/>

      <div>
        Please click below to start paying for your membership when you are ready.
        <br/><br/>
        <ActionButton onClick={newSubscription} rpcState={getNewSubscriptionLink.rpcState}>
          Start your Membership</ActionButton>
        <div className={'InputHelpText'}>You'll be redirected to Stripe for payment</div>
        {getNewSubscriptionLink.rpcState.state == 'error' &&
            <div className={'ErrorText'}> There was an error. Please try again. </div>
        }
      </div>

    </KensuObjectEditor>
  }

  const Content = () => {
    if (getMembershipRpc.rpcState.state == 'pending') {
      return <div className={'ProviderToolAreaContent'}>Loading your membership status....</div>;
    }

    if (getMembershipRpc.rpcState.state == 'error') {
      return <div className={'ProviderToolAreaContent ErrorText'}>Error loading your membership status. Please try again later</div>;
    }

    let membership = getMembershipRpc.membership;

    if (membership?.isActive) {

      // This is an ACTIVE membership. We don't need to do the intended membership editor.
      return <div className={'ProviderToolAreaContent'}>
        <h2>Subscription status</h2>
        <SubscriptionStatus/>
      </div>

    } else {

      // NO active membership. Show the editor for the intended membership
      // and a button to start the thing.
      return <>
        <IntendedMembershipEditor/>
      </>
    }

  }

  return <>

    <TabbedPageHeader pageTitle={'Settings'} tabs={useSettingsTabs()}/>
    <Content/>
  </>
}
